// Data access: TanStack Query over the RLS-protected views and RPCs. The browser uses the anon key + the user's
// session only; every row it can read or write is decided by Postgres RLS / SECURITY DEFINER RPCs.

import { keepPreviousData, useQuery } from '@tanstack/react-query';
import type { Polygon } from 'geojson';
import { ACCESS_CHECK_MS } from '@fleetspoke/core/auth';
import type { SiteType } from '@fleetspoke/core/places';
import type { AssetOverview, HistoryEvent, SiteOverview, Whoami } from '@fleetspoke/core/types';
import { NONE } from '../lib/assetFilters';
import { supabase } from '../lib/supabase';

export type { AssetOverview, HistoryEvent, SiteOverview, Whoami } from '@fleetspoke/core/types';

export interface SiteGeometry {
  site_id: string;
  geom_geojson: Polygon | null;
  centroid_lng: number | null;
  centroid_lat: number | null;
  radius_m: number | null;
}

async function rows<T>(
  query: PromiseLike<{ data: unknown; error: { message: string } | null }>,
): Promise<T> {
  const { data, error } = await query;
  if (error) throw new Error(error.message);
  return data as T;
}

export const keys = {
  whoami: ['whoami'] as const,
  sites: ['sites'] as const,
  siteGeometry: ['site-geometry'] as const,
  board: (siteId: string) => ['board', siteId] as const,
  assets: (f: AssetFilters, states: readonly string[] = []) =>
    [
      'assets',
      f.search,
      f.site ?? '',
      f.category ?? '',
      states.join(','),
      f.registeredSince ?? '',
      f.newestFirst ? 'new' : '',
      f.certState ?? '',
      f.outOfService ? 'oos' : '',
    ] as const,
  asset: (id: string) => ['asset', id] as const,
  history: (assetId: string | undefined, siteId?: string) =>
    ['history', assetId ?? '', siteId ?? ''] as const,
  categories: ['categories'] as const,
  members: ['members'] as const,
};

export function useWhoami(enabled: boolean) {
  return useQuery({
    queryKey: keys.whoami,
    enabled,
    queryFn: () => rows<Whoami>(supabase().rpc('whoami')),
    // notice a role change / deactivation while the tab stays open (focus and refused calls also re-read it)
    refetchInterval: ACCESS_CHECK_MS,
  });
}

export function useSites(enabled = true) {
  return useQuery({
    queryKey: keys.sites,
    enabled,
    queryFn: () => rows<SiteOverview[]>(supabase().from('site_overview').select('*').order('name')),
  });
}

/** Sites still in use: pickers, lists and the map. Name lookups keep the whole list (history names old sites). */
export function activeSites(sites: SiteOverview[] | undefined): SiteOverview[] {
  return (sites ?? []).filter((s) => s.is_active);
}

/** A site's own settings (the edit dialog, and the Locations tab's read-only details). */
export interface SiteRow {
  id: string;
  name: string;
  type: SiteType;
  t_exit_s: number;
  t_missing_s: number;
  // stock count schedule (CR_20260914): every N days from a start date, company timezone
  count_every_days: number | null;
  count_start_date: string | null;
  count_due_time: string;
  count_remind_time: string;
  count_skip_days: number[];
  project_id: string | null;
  is_active: boolean;
}

export function useSiteRows() {
  return useQuery({
    queryKey: ['site-rows'],
    queryFn: () =>
      rows<SiteRow[]>(
        supabase()
          .from('sites')
          .select(
            'id, name, type, t_exit_s, t_missing_s, count_every_days, count_start_date, count_due_time, count_remind_time, count_skip_days, project_id, is_active',
          )
          .order('name'),
      ),
  });
}

export function useSiteGeometry() {
  return useQuery({
    queryKey: keys.siteGeometry,
    queryFn: () => rows<SiteGeometry[]>(supabase().from('site_geometry').select('*')),
  });
}

/** Everything that belongs on a site board: custody here, or physically here now. */
export function useSiteBoard(siteId: string) {
  return useQuery({
    queryKey: keys.board(siteId),
    enabled: !!siteId,
    queryFn: () =>
      rows<AssetOverview[]>(
        supabase()
          .from('asset_overview')
          .select('*')
          .or(`custody_site_id.eq.${siteId},presence_site_id.eq.${siteId}`)
          // a retired asset is no longer anywhere (CR_20260920_Asset_Register)
          .eq('asset_status', 'active')
          .order('asset_code'),
      ),
  });
}

export interface AssetFilters {
  search: string;
  /** '' every site · 'none' no site · otherwise at or belonging to that site (as the site board counts it) */
  site?: string;
  /** '' every category · 'none' uncategorised · otherwise that category */
  category?: string;
  /** registered at or after this instant (ISO) — "Registered in the last 7 days" (CR_20260920_Asset_Register §9) */
  registeredSince?: string;
  /** newest registrations first instead of by code */
  newestFirst?: boolean;
  /** asset_overview.cert_state — the worst state of the certificates its category requires (CR_20260921 §7.3) */
  certState?: string;
  /** only assets out of service (CR_20260921 T2) */
  outOfService?: boolean;
}

interface Narrowable<T> {
  eq(column: string, value: string): T;
  is(column: string, value: null): T;
  or(filters: string): T;
  gte(column: string, value: string): T;
}

// Filters other than the display state (the state chips count under these). Two or() filters are ANDed.
function narrowAssets<T extends Narrowable<T>>(q: T, f: AssetFilters): T {
  const s = f.search.trim().replace(/[%,()]/g, ' ');
  if (s) q = q.or(`asset_code.ilike.%${s}%,description.ilike.%${s}%`);
  if (f.site === NONE) q = q.is('custody_site_id', null).is('presence_site_id', null);
  else if (f.site) q = q.or(`custody_site_id.eq.${f.site},presence_site_id.eq.${f.site}`);
  if (f.category === NONE) q = q.is('category_id', null);
  else if (f.category) q = q.eq('category_id', f.category);
  if (f.registeredSince) q = q.gte('created_at', f.registeredSince);
  if (f.certState) q = q.eq('cert_state', f.certState);
  if (f.outOfService) q = q.eq('service_state', 'out_of_service');
  return q;
}

/** The two "removed from service" filters; they select by retire_stage, not by display state. */
export const RETIRED_STATES = ['awaiting_tag_removal', 'retired'] as const;

interface StateNarrowable<T> {
  eq(column: string, value: string): T;
  in(column: string, values: string[]): T;
}

// A retired asset is out of the register's normal view: it shows only when a retired filter asks for it.
function narrowStates<T extends StateNarrowable<T>>(q: T, states: readonly string[]): T {
  const retired = states.filter((s) => (RETIRED_STATES as readonly string[]).includes(s));
  if (retired.length) return q.in('retire_stage', retired);
  q = q.eq('asset_status', 'active');
  return states.length ? q.in('display_state', [...states]) : q;
}

/** Rows per page on the Assets list; the first is the default. */
export const ASSET_PAGE_SIZES = [30, 50, 100] as const;

export interface AssetPage {
  /** 0-based */
  page: number;
  size: number;
}

/**
 * One page of the assets matching the filters, optionally only some display states (?state= on the Assets page).
 * The total for the pager comes from `useAssetCounts`. asset_id breaks ties so a page never repeats or skips a row.
 */
export function useAssets(
  f: AssetFilters,
  states: readonly string[] = [],
  { page, size }: AssetPage = { page: 0, size: ASSET_PAGE_SIZES[0] },
) {
  return useQuery({
    queryKey: [...keys.assets(f, states), 'page', page, size],
    placeholderData: keepPreviousData,
    queryFn: () =>
      rows<AssetOverview[]>(
        narrowStates(
          narrowAssets(
            supabase()
              .from('asset_overview')
              .select('*')
              .order(f.newestFirst ? 'created_at' : 'asset_code', { ascending: !f.newestFirst })
              .order('asset_id')
              .range(page * size, page * size + size - 1),
            f,
          ),
          states,
        ),
      ),
  });
}

async function countAssets(f: AssetFilters | null, states: readonly string[]): Promise<number> {
  let q = supabase().from('asset_overview').select('asset_id', { count: 'exact', head: true });
  if (f) q = narrowAssets(q, f);
  q = narrowStates(q, states);
  const { count, error } = await q;
  if (error) throw new Error(error.message);
  return count ?? 0;
}

/**
 * Counted in the database: one number per state chip under the other filters (`[]` = all states), the account's
 * total with no filter at all, and `current` — the filters and `states` the list shows (the pager's total; a chip's
 * number when `states` is a chip, else counted once more).
 */
export function useAssetCounts(
  f: AssetFilters,
  chips: readonly (readonly string[])[],
  states: readonly string[] = [],
) {
  const chipOf = chips.findIndex((c) => c.join(',') === states.join(','));
  return useQuery({
    queryKey: [
      ...keys.assets(f, []),
      'counts',
      chips.map((c) => c.join(',')).join('|'),
      chipOf >= 0 ? '' : states.join(','),
    ],
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const [total, own, ...byChip] = await Promise.all([
        countAssets(null, []),
        chipOf >= 0 ? Promise.resolve(-1) : countAssets(f, states),
        ...chips.map((c) => countAssets(f, c)),
      ]);
      return { total: total!, byChip, current: chipOf >= 0 ? byChip[chipOf]! : own! };
    },
  });
}

export function useAsset(assetId: string) {
  return useQuery({
    queryKey: keys.asset(assetId),
    queryFn: () =>
      rows<AssetOverview>(
        supabase().from('asset_overview').select('*').eq('asset_id', assetId).single(),
      ),
  });
}

export function useHistory(assetId?: string, siteId?: string, limit = 50) {
  return useQuery({
    queryKey: keys.history(assetId, siteId),
    queryFn: () => {
      let q = supabase()
        .from('asset_location_history')
        .select(
          'id, event, site_id, vehicle_ref, at, source, asset_id, sites(name), assets(asset_code)',
        )
        .order('at', { ascending: false })
        .limit(limit);
      if (assetId) q = q.eq('asset_id', assetId);
      if (siteId) q = q.eq('site_id', siteId);
      return rows<(HistoryEvent & { asset_id: string; assets: { asset_code: string } | null })[]>(
        q,
      );
    },
  });
}

/**
 * Arrivals, departures, missing / found at any of a place's sites (a warehouse, or a project's locations) in
 * [fromIso, toIso), newest first. The Transfers tab lists the ones no transfer explains.
 */
export function usePlaceHistory(
  siteIds: readonly string[],
  fromIso: string,
  toIso: string,
  limit: number,
) {
  const ids = [...siteIds].sort();
  return useQuery({
    queryKey: ['history', '', ids.join(','), 'place', fromIso, toIso],
    enabled: ids.length > 0 && !!fromIso && !!toIso,
    queryFn: () =>
      rows<(HistoryEvent & { asset_id: string; assets: { asset_code: string } | null })[]>(
        supabase()
          .from('asset_location_history')
          .select(
            'id, event, site_id, vehicle_ref, at, source, asset_id, sites(name), assets(asset_code)',
          )
          .in('site_id', ids)
          .gte('at', fromIso)
          .lt('at', toIso)
          .order('at', { ascending: false })
          .limit(limit),
      ),
  });
}

export function useCategories() {
  return useQuery({
    queryKey: keys.categories,
    queryFn: () =>
      rows<{ id: string; name: string; default_tier: 'qr' | 'ble' | 'gps' }[]>(
        supabase().from('asset_categories').select('id, name, default_tier').order('name'),
      ),
  });
}
